import React, { useState, useEffect, createContext, useContext } from 'react';
// Headerコンポーネントと他のコンポーネントで使うアイコンはそれぞれのファイルでインポート
// App.jsからは基本的なReactフックのみをインポート
// import { BarChart2, User } from 'lucide-react'; // App.jsからは不要になる

// 認証サービスをインポート
import {
    initializeAndMonitorAuth,
    logout
} from './services/AuthService';

// Firebase設定をインポート
import { firebaseAppId } from './configs/firebaseConfig';

// 各コンポーネントを新しく作成したファイルからインポート
import SignInScreen from './components/SignIn';
import Header from './components/Header'; // Headerコンポーネントをインポート
import Dashboard from './components/Dashboard';
import TransactionInput from './components/TransactionInput';
import TransactionList from './components/TransactionList';
import Reports from './components/Reports';
import SettingsPage from './components/Settings';
import Feedback from './components/Feedback'; // Feedbackコンポーネントをインポート
import ReceiptScanner from './components/ReceiptScanner'; // ReceiptScannerコンポーネントをインポート

// AdSense設定をインポート
import adsConfig from './configs/adsConfig';

// Firebase Context for easy access to auth and db instances
export const FirebaseContext = createContext(null);

// AdSense広告を表示するためのコンポーネント
const AdSenseAd = ({ slotId, format = 'auto', responsive = 'true' }) => {
    useEffect(() => {
        if (adsConfig.enableAds) {
            try {
                // window.adsbygoogle がロードされていることを確認し、広告をプッシュ
                (window.adsbygoogle = window.adsbygoogle || []).push({});
            } catch (e) {
                console.error("AdSenseエラー:", e);
            }
        }
    }, [slotId]); // slotIdが変わったら再ロードを試みる

    if (!adsConfig.enableAds) {
        // 広告が無効な場合は何も表示しない
        // または、開発環境用のプレースホルダーを表示することも可能
        // return <div className="bg-gray-200 text-gray-700 p-4 text-center rounded-md">広告プレースホルダー (開発中)</div>;
        return null;
    }

    return (
        <ins className="adsbygoogle"
            style={{ display: 'block' }}
            data-ad-client={adsConfig.publisherId}
            data-ad-slot={slotId}
            data-ad-format={format}
            data-full-width-responsive={responsive}>
        </ins>
    );
};


const App = () => {
    const [db, setDb] = useState(null);
    const [auth, setAuth] = useState(null);
    const [userId, setUserId] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [currentPage, setCurrentPage] = useState('dashboard');
    const [isMobile, setIsMobile] = useState(false); // モバイル判定の状態

    // レシートスキャンで取得したデータ
    const [scannedReceiptData, setScannedReceiptData] = useState(null);

    // モバイル判定のロジック
    useEffect(() => {
        const checkIsMobile = () => {
            // 幅が768px未満をモバイルと判定（Tailwindのmdブレークポイント未満）
            setIsMobile(window.innerWidth < 768);
        };

        checkIsMobile(); // 初期ロード時に一度チェック
        window.addEventListener('resize', checkIsMobile); // ウィンドウサイズ変更時に再チェック

        return () => window.removeEventListener('resize', checkIsMobile);
    }, []);

    useEffect(() => {
        const setupAuthMonitoring = async () => {
            try {
                const { db: firestoreDb, auth: firebaseAuth } = await initializeAndMonitorAuth((user) => {
                    if (user) {
                        setUserId(user.uid);
                    } else {
                        setUserId(null);
                    }
                    setLoading(false);
                });
                setDb(firestoreDb);
                setAuth(firebaseAuth);
            } catch (err) {
                console.error("Firebase初期化に失敗しました:", err);
                setError("Firebaseの初期化に失敗しました。 (" + err.message + ")");
                setLoading(false);
            }
        };

        setupAuthMonitoring();
    }, []);

    const handleLogout = async () => {
        try {
            await logout();
            setUserId(null);
            setCurrentPage('dashboard');
        } catch (error) {
            console.error("ログアウトエラー:", error);
        }
    };

    // レシートスキャン完了時のコールバック
    const handleReceiptScanComplete = (data) => {
        setScannedReceiptData(data); // スキャンされたデータを保持
        setCurrentPage('transactionInput'); // 取引入力ページに戻る
    };

    if (loading) {
        return (
            <div className="flex items-center justify-center min-h-screen bg-gray-100">
                <div className="text-xl text-gray-700">Loading...</div>
            </div>
        );
    }

    if (error) {
        return (
            <div className="flex items-center justify-center min-h-screen bg-red-100 text-red-700 p-4 rounded-md">
                <p>エラーが発生しました: {error}</p>
            </div>
        );
    }

    if (!userId) {
        return <SignInScreen />;
    }

    return (
        <FirebaseContext.Provider value={{ db, auth, userId, handleLogout, firebaseAppId, isMobile, scannedReceiptData, setScannedReceiptData }}>
            <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-100 font-sans antialiased">
                <Header userId={userId} setCurrentPage={setCurrentPage} />
                <main className="container mx-auto px-4 py-8">
                    {currentPage === 'dashboard' && <Dashboard setCurrentPage={setCurrentPage} />}
                    {currentPage === 'transactionInput' && (
                        // 取引入力コンポーネントにスキャンデータを渡す
                        <TransactionInput scannedData={scannedReceiptData} setScannedData={setScannedReceiptData} setCurrentPage={setCurrentPage} />
                    )}
                    {currentPage === 'transactionList' && <TransactionList />}
                    {currentPage === 'reports' && <Reports />}
                    {currentPage === 'settings' && <SettingsPage />}
                    {currentPage === 'receiptScan' && isMobile && (
                        // モバイルの場合のみReceiptScannerを表示
                        <ReceiptScanner onScanComplete={handleReceiptScanComplete} />
                    )}
                    
                    {adsConfig.enableAds && (
                        <div className="mt-8 p-4 bg-white rounded-xl shadow-lg text-center" hidden>
                            <h3 className="text-xl font-semibold text-gray-800 mb-4">スポンサーコンテンツ</h3>
                            <AdSenseAd slotId={adsConfig.adUnitIds.displayAd} />
                        </div>
                    )}

                    {/* フィードバックコンポーネントをここに追加 */}
                    <Feedback />

                </main>
            </div>
        </FirebaseContext.Provider>
    );
};

export default App;
